import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { PrimaryButton, SecondaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { IconApproved, IconCancel } from 'assets/img';
import { accreditationsAlerts, useAlert } from 'shared/components/alerts';
import { useUpdateAccreditation } from '../../hooks/use-update-accreditation';
import type { AccreditationRejectionReason } from '../../../../../services/accreditations-service.types';
import {
  AccreditationDetailFormValues,
  UpdateAccreditationButtonProps,
} from '../accreditations-detail.types';

export const useAccreditationDetailUpdateAction = ({
  flexWorkerId,
  certificateId,
  expirationDate,
  startDate,
  accreditationType,
  accreditationState,
  rejectionReason,
  closeModal,
}: UpdateAccreditationButtonProps) => {
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const { alertSuccess, alertError } = useAlert();
  const mutation = useUpdateAccreditation();
  const { trigger } = useFormContext<AccreditationDetailFormValues>();

  const mutationData = useMemo(
    () => ({
      flexWorkerId,
      certificateId,
      expirationDate,
      startDate,
      accreditationType,
      ...(accreditationState === 'rejected' && rejectionReason
        ? { rejectionReason: rejectionReason as AccreditationRejectionReason }
        : {}),
    }),
    [
      certificateId,
      flexWorkerId,
      expirationDate,
      startDate,
      accreditationType,
      accreditationState,
      rejectionReason,
    ]
  );

  const handleUpdateAccreditation = async () => {
    if (accreditationState === 'rejected') {
      const isRejectionReasonValid = await trigger('rejectionReason');
      if (!isRejectionReasonValid) {
        return;
      }
    }

    openDialog(
      {
        title: 'Weet u zeker dat u de vervaldatum van het certificaat wilt bijwerken?',
        secondaryButton: (props) => (
          <PrimaryButton iconColorMode="fill" startIcon={<IconCancel />} {...props}>
            Annuleer
          </PrimaryButton>
        ),
        primaryButton: (props) => (
          <SecondaryButton startIcon={<IconApproved />} {...props}>
            Bevestig
          </SecondaryButton>
        ),
      },
      () => {
        disableDialog();
        mutation.mutate(mutationData, {
          onSuccess: () => {
            closeDialog();
            closeModal();
            alertSuccess(accreditationsAlerts.success.updated);
          },
          onError: (e) => alertError(e),
          onSettled: () => {
            enableDialog();
          },
        });
      }
    );
  };

  return { handleUpdateAccreditation };
};
